import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, AnnouncementContext } from 'context';
import { Button, Modal, notification, Popconfirm, Table } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileProtectOutlined,
  MailOutlined,
  PlusOutlined,
  TagOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-layout';
import CheckAuth from 'components/common/CheckAuth';
import 'antd/dist/reset.css';
import announcementService from 'services/announcement.service';
import AddAnnouncement from './AddAnnouncement';
import EditAnnouncement from './EditAnnouncement';
import roles from '../../helpers/roles';
import authorities from '../../helpers/authorities';

const Announcements = () => {
  const context = useContext(AuthContext);
  const { selectAnnouncement } = useContext(AnnouncementContext);
  const announcementContext = useContext(AnnouncementContext);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [viewVisible, setViewVisible] = useState(false);
  const [addVisible, setAddVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const currentUserRole = context.currentUser?.role?.name;

  const getCurrentAnnouncement = async () => {
    try {
      setLoading(true);
      const { payload } = await announcementService.getAnnouncements();
      setLoading(false);
      setAnnouncements(payload);
    } catch (error) {
      setLoading(false);
      notification.error({
        message: 'İşlem Başarısız',
        description: error,
      });
    }
  };

  useEffect(() => {
    getCurrentAnnouncement();
  }, []);

  const handleCloseAddForm = () => {
    setAddVisible(false);
  };

  const handleCloseEditForm = () => {
    setEditVisible(false);
  };

  const handleCloseViewForm = () => {
    setViewVisible(false);
  };

  const handleSubmitAddForm = async (values) => {
    setLoading(true);
    setSubmitting(true);
    try {
      await announcementService.addAnnouncement(values);
      notification.success({
        message: 'İşlem Başarılı',
        description: 'Duyuru eklendi!',
      });
      setLoading(false);
      setSubmitting(false);
      handleCloseAddForm();
      await getCurrentAnnouncement();
    } catch (error) {
      console.log(error.message);
      notification.error({
        message: 'İşlem Başarısız',
        description: 'Duyuru eklenemedi!',
      });
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleSubmitEditForm = async (values) => {
    setLoading(true);
    setSubmitting(true);
    const newValues = {
      ...values,
    };
    try {
      await announcementService.updateAnnouncement(newValues);
      notification.success({
        message: 'İşlem Başarılı',
        description: 'Duyuru güncellendi!',
      });
      setLoading(false);
      setSubmitting(false);
      handleCloseEditForm();
      await getCurrentAnnouncement();
    } catch (error) {
      console.log(error.message);
      notification.error({
        message: 'İşlem Başarısız',
        description: 'Duyuru güncellenemedi!',
      });
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    setLoading(true);
    setSubmitting(true);
    try {
      await announcementService.deleteAnnouncement(id);
      notification.success({
        message: 'İşlem Başarılı',
        description: 'Duyuru silindi!',
      });
      setLoading(false);
      setSubmitting(false);
      await getCurrentAnnouncement();
    } catch (error) {
      console.log(error.message);
      notification.error({
        message: 'İşlem Başarısız',
        description: 'Duyuru silinemedi!',
      });
      setLoading(false);
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: <div className="table-title-calibri-bold-12">Duyuru Tipi</div>,
      align: 'center',
      width: 100,
      dataIndex: 'type',
      key: 'type',
      render: (id, record) => {
        switch (record.type) {
          case 'Genel Duyuru':
            return (
              <span className="table-title-calibri-bold-12">
                <MailOutlined
                  style={{
                    fontSize: 20,
                    marginRight: 15,
                    marginLeft: 3,
                  }}
                />{' '}
                Genel Duyuru
              </span>
            );
          case 'Yasal Uyarı':
            return (
              <span className="table-title-calibri-bold-12">
                <FileProtectOutlined
                  style={{
                    fontSize: 20,
                    marginRight: 15,
                    marginLeft: -10,
                  }}
                />{' '}
                Yasal Uyarı
              </span>
            );
          case 'Tebrik & Kutlama':
            return (
              <span className="table-title-calibri-bold-12">
                <TeamOutlined
                  style={{
                    fontSize: 20,
                    marginRight: 15,
                    marginLeft: 3,
                  }}
                />{' '}
                Tebrik & Kutlama
              </span>
            );
          case 'Kampanya':
            return (
              <span className="table-title-calibri-bold-12">
                <TagOutlined
                  style={{
                    fontSize: 20,
                    marginRight: 15,
                    marginLeft: 3,
                  }}
                />{' '}
                Kampanya
              </span>
            );
          default:
            return '';
        }
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Duyuru Başlığı</div>,
      dataIndex: 'title',
      key: 'title',
      align: 'center',
      width: 100,
      render: (text, row) => {
        return <span className="table-title-calibri-bold-12">{row?.title}</span>;
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Duyuru İçeriği</div>,
      dataIndex: 'text',
      key: 'text',
      align: 'center',
      width: 150,
      render: (text, row) => {
        return <span className="table-title-calibri-bold-12">{row?.text}</span>;
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">İşlemler</div>,
      dataIndex: 'identifier',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 200,
      render: (id, record) => (
        <div>
          <CheckAuth authorityCode={authorities.UPDATE_ANNOUNCEMENTS}>
            <Button
              className="mr-3"
              type="ghost"
              onClick={() => {
                setEditVisible(true);
                selectAnnouncement(record);
              }}
            >
              <EditOutlined />
            </Button>
          </CheckAuth>

          {currentUserRole !== roles.PAYE_ADMIN ? (
            <Button
              type="ghost"
              onClick={() => {
                setViewVisible(true);
                selectAnnouncement(record);
              }}
            >
              <EyeOutlined />
            </Button>
          ) : null}

          <CheckAuth authorityCode={authorities.DELETE_ANNOUNCEMENTS}>
            <Popconfirm
              title="Duyuruyu silmek istediğinize emin misiniz?"
              okText="Evet"
              cancelText="Hayır"
              onConfirm={async () => {
                await handleDeleteAnnouncement(id);
              }}
              placement="topRight"
            >
              <Button className="ml-3" type="primary" danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </CheckAuth>
        </div>
      ),
    },
  ];

  const AnnouncementTitle = announcementContext?.selectedAnnouncement;

  return (
    <PageContainer
      title="Duyurular"
      // onBack={() => history.goBack()}
      extra={[
        <CheckAuth authorityCode={authorities.ADD_ANNOUNCEMENTS} key="1">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setAddVisible(true);
            }}
          >
            Yeni Duyuru
          </Button>
        </CheckAuth>,
      ]}
    >
      <Table
        loading={loading}
        dataSource={announcements}
        columns={columns}
        pagination={false}
        rowKey="identifier"
      />

      <AddAnnouncement
        visible={addVisible}
        submitting={submitting}
        onOk
        onCancel
        handleCloseForm={handleCloseAddForm}
        handleSubmitForm={handleSubmitAddForm}
      />
      <EditAnnouncement
        visible={editVisible}
        submitting={submitting}
        onOk
        onCancel
        handleCloseForm={handleCloseEditForm}
        handleSubmitForm={handleSubmitEditForm}
      />
      <Modal
        title="Duyuru Görüntüleme"
        open={viewVisible}
        onOk={handleCloseViewForm}
        onCancel={handleCloseViewForm}
      >
        <div style={{ fontWeight: 'bolder' }}>Duyuru Tipi: </div>
        <div className="mt-2">{AnnouncementTitle?.type}</div>
        <div className="mt-4" style={{ fontWeight: 'bolder' }}>
          Duyuru Başlığı:{' '}
        </div>
        <div className="mt-2">{AnnouncementTitle?.title}</div>
        <div className="mt-4" style={{ fontWeight: 'bolder' }}>
          Duyuru İçeriği:{' '}
        </div>
        <div className="mt-2">{AnnouncementTitle?.text}</div>
      </Modal>
    </PageContainer>
  );
};

export default Announcements;
